import React from 'react';
import { stores } from 'state/stores';
import styled, { ThemeProps } from 'styled-components';
import { Card } from 'styleguide/Card';
import { colors } from 'styleguide/colors';
import { H1, TextLG, TextMD } from 'styleguide/Texts';
import IconDownload from './assets/icon-download.svg';
import IconLogin from './assets/icon-login.svg';
import IconWorkout from './assets/icon-workout.svg';
import DownloadQRCode from './assets/qr-code.svg';
import DownloadAppstore from 'assets/download-appstore.svg';
import DownloadGooglePlay from 'assets/download-googleplay.svg';
import { DividerLine } from 'styleguide/DividerLine';
import { Theme } from 'styleguide/types';

const Container = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: space-around;
  overflow: hidden;
  margin-bottom: 16px;
`;

const HeaderContainer = styled.div`
  background-color: ${(props: ThemeProps<Theme>) =>
    props.theme.name === 'light' ? colors.neutral100 : colors.neutral700};
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: center;
  padding: 0 48px;
  margin-bottom: 24px;
  @media (max-width: 1024px) {
    flex-direction: column;
    text-align: center;
    padding: 24px 48px 24px 48px;
    margin-bottom: 8px;
  }
`;

const WelcomeImage = styled.div`
  background: url(/images/empty-feed-welcome.png) no-repeat;
  background-size: contain;
  width: 242px;
  height: 192px;
  margin-top: 24px;
  margin-left: 48px;
  @media (max-width: 1024px) {
    display: none;
  }
`;

const ItemContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 24px 48px;
  @media (max-width: 1024px) {
    flex-direction: column;
    text-align: center;
    padding: 16px 48px 8px 48px;
  }
`;

const ItemIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border: 1px solid ${colors.primary500};
  border-radius: 32px;
  margin-right: 32px;
  @media (max-width: 1024px) {
    margin: 0;
    width: 56px;
    height: 56px;
    border-radius: 28px;
  }
`;

const ItemText = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  @media (max-width: 1024px) {
    margin: 8px 0;
  }
`;

const DownloadQRCodeContainer = styled.div`
  display: flex;
  width: 88px;
  height: 88px;
  align-items: center;
  justify-content: center;
  border: 1px solid
    ${(props: ThemeProps<Theme>) =>
      props.theme.name === 'light' ? colors.neutral100 : colors.neutral400};
  border-radius: 8px;
  text-align: center;
  margin: 0 24px;
  @media (max-width: 1024px) {
    display: none;
  }
`;

const AppStoreButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  @media (max-width: 1024px) {
    margin-bottom: 8px;
  }
`;

const ItemDivider = styled(DividerLine)`
  width: calc(100% - 96px);
  align-self: center;
  @media (max-width: 1024px) {
    width: calc(100% - 48px);
  }
`;

export const MobileCTACell = () => {
  return (
    <Container>
      <HeaderContainer>
        <ItemText>
          <H1 style={{ marginBottom: 8 }}>
            Hello <span style={{ color: colors.primary500 }}>{stores.account.username}</span>,
            welcome to Hevy!
          </H1>
          <TextMD>To take full advantage of Hevy complete the following steps:</TextMD>
        </ItemText>
        <WelcomeImage />
      </HeaderContainer>
      <ItemContainer>
        <ItemIcon>
          <IconDownload />
        </ItemIcon>
        <ItemText>
          <TextLG>Download the Hevy mobile app</TextLG>
        </ItemText>
        <DownloadQRCodeContainer>
          <DownloadQRCode />
        </DownloadQRCodeContainer>
        <AppStoreButtons>
          <a href="https://hevyapp.app.link/KktuliLeKtb">
            <DownloadAppstore style={{ height: 40 }} />
          </a>
          <a href="https://hevyapp.app.link/tIYiWyJeKtb">
            <DownloadGooglePlay style={{ height: 40 }} />
          </a>
        </AppStoreButtons>
      </ItemContainer>
      <ItemDivider />
      <ItemContainer>
        <ItemIcon>
          <IconLogin />
        </ItemIcon>
        <ItemText>
          <TextLG style={{ marginBottom: 4 }}>Log in with the account you just created</TextLG>
          <TextMD>Make sure you tap on "Already have an account? Login" button</TextMD>
        </ItemText>
      </ItemContainer>
      <ItemDivider />
      <ItemContainer style={{ marginBottom: 8 }}>
        <ItemIcon>
          <IconWorkout />
        </ItemIcon>
        <ItemText>
          <TextLG style={{ marginBottom: 4 }}>Log your first workout with the Hevy App</TextLG>
          <TextMD>Enjoy access to Hevy on both your phone and the web app</TextMD>
        </ItemText>
      </ItemContainer>
    </Container>
  );
};
